import { useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Button, Grid, Chip, Stack, Divider, Container, Modal, IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GitHubIcon from '@mui/icons-material/GitHub';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import portfolio from '../data/portfolio';

export default function PortfolioPage() {
  const { id } = useParams<{ id: string }>();
  const project = portfolio.find((p) => p.id === id);
  const [lightbox, setLightbox] = useState<{ src: string; label: string } | null>(null);

  if (!project) {
    return (
      <Container sx={{ py: 16, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ mb: 3 }}>Project not found</Typography>
        <Button component={RouterLink} to="/#portfolio" startIcon={<ArrowBackIcon />} variant="outlined">
          Back to Portfolio
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ background: '#0d0a12', minHeight: '100vh', pt: 10 }}>

      {/* ── Back nav ── */}
      <Box sx={{ px: { xs: 3, md: 8 }, pt: { xs: 4, md: 6 }, pb: 4 }}>
        <Button
          component={RouterLink}
          to="/#portfolio"
          startIcon={<ArrowBackIcon />}
          sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
        >
          All Projects
        </Button>
      </Box>

      {/* ── Hero gallery — full bleed ── */}
      {(() => {
        const heroImages = project.heroImages ?? project.gallery.map((g) => g.image);
        return heroImages.length > 0 ? (
          <Box
            sx={{
              display: 'flex',
              overflowX: 'auto',
              scrollSnapType: 'x mandatory',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {heroImages.map((src, i) => (
              <Box
                key={src}
                onClick={() => setLightbox({ src, label: project.heroImages ? '' : (project.gallery[i]?.label ?? '') })}
                sx={{
                  flexShrink: 0,
                  scrollSnapAlign: 'start',
                  cursor: 'zoom-in',
                  position: 'relative',
                  background: '#08060e',
                  '&:hover .hero-overlay': { opacity: 1 },
                }}
              >
                <Box
                  component="img"
                  src={src}
                  alt={project.title}
                  sx={{
                    display: 'block',
                    height: { xs: '62vh', sm: '76vh', md: '88vh' },
                    width: 'auto',
                    maxWidth: '100vw',
                    objectFit: 'contain',
                  }}
                />
                <Box className="hero-overlay" sx={{
                  position: 'absolute', inset: 0,
                  background: 'rgba(0,0,0,0.28)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: 0, transition: 'opacity 0.2s',
                  pointerEvents: 'none',
                }}>
                  <ZoomInIcon sx={{ fontSize: '2.8rem', color: '#fff' }} />
                </Box>
              </Box>
            ))}
          </Box>
        ) : null;
      })()}

      {/* scroll hint dots */}
      {(project.heroImages ?? project.gallery).length > 1 && (
        <Stack direction="row" spacing={0.75} justifyContent="center" sx={{ mt: 2.5 }}>
          {(project.heroImages ?? project.gallery.map((g) => g.image)).map((_, i) => (
            <Box key={i} sx={{
              width: 6, height: 6, borderRadius: '50%',
              background: i === 0 ? project.color : `${project.color}33`,
            }} />
          ))}
        </Stack>
      )}

      {/* ── Project title block ── */}
      <Box sx={{ maxWidth: 780, mx: 'auto', px: { xs: 3, md: 4 }, pt: { xs: 8, md: 12 }, pb: { xs: 6, md: 8 }, textAlign: 'center' }}>
        <Stack direction="row" spacing={1.5} justifyContent="center" alignItems="center" sx={{ mb: 3 }}>
          <Chip label={project.category} size="small" sx={{
            background: `${project.color}18`, border: `1px solid ${project.color}44`, color: project.color,
          }} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{project.year}</Typography>
        </Stack>

        <Typography variant="h1" sx={{
          fontSize: { xs: '2.6rem', sm: '3.8rem', md: '5rem' },
          fontWeight: 700,
          lineHeight: 1.05,
          letterSpacing: '-0.02em',
          mb: 3,
        }}>
          {project.title}
        </Typography>

        <Typography sx={{
          color: 'text.secondary', fontSize: { xs: '1rem', md: '1.2rem' },
          lineHeight: 1.8, mb: 4,
        }}>
          {project.desc}
        </Typography>

        <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center" sx={{ gap: 1, mb: 5 }}>
          {project.tags.map((tag) => (
            <Chip key={tag} label={tag} size="small" sx={{
              background: `${project.color}10`, border: `1px solid ${project.color}30`, color: project.color,
            }} />
          ))}
        </Stack>

        <Stack direction="row" spacing={2} flexWrap="wrap" justifyContent="center" sx={{ gap: 2 }}>
          {project.links.map((link) => (
            <Button
              key={link.label}
              variant={link.url ? 'contained' : 'outlined'}
              endIcon={link.label.toLowerCase().includes('github') ? <GitHubIcon /> : <OpenInNewIcon />}
              href={link.url ?? '#'}
              sx={link.url ? {
                background: `linear-gradient(135deg, ${project.color}, ${project.color}bb)`,
                '&:hover': { boxShadow: `0 0 20px ${project.color}44` },
              } : {
                borderColor: `${project.color}44`, color: project.color,
                '&:hover': { borderColor: project.color, background: `${project.color}10` },
              }}
            >
              {link.label}
            </Button>
          ))}
        </Stack>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,110,180,0.08)', mx: { xs: 3, md: 10 } }} />

      {/* ── Overview ── */}
      <Box sx={{ maxWidth: 680, mx: 'auto', px: { xs: 3, md: 4 }, py: { xs: 8, md: 12 } }}>
        <Typography variant="overline" sx={{ color: project.color, letterSpacing: 4, fontSize: '0.7rem' }}>
          OVERVIEW
        </Typography>
        {project.longDesc.map((para, i) => (
          <Typography key={i} variant="body1" sx={{
            color: 'text.secondary', lineHeight: 2,
            mt: i === 0 ? 2 : 3, fontSize: '1.05rem',
          }}>
            {para}
          </Typography>
        ))}
      </Box>

      {/* ── Specs ── */}
      <Box sx={{ maxWidth: 780, mx: 'auto', px: { xs: 3, md: 4 }, pb: { xs: 8, md: 12 } }}>
        <Typography variant="overline" sx={{ color: project.color, letterSpacing: 4, fontSize: '0.7rem' }}>
          SPECS
        </Typography>
        <Grid container sx={{ mt: 2 }}>
          {project.specs.map((spec) => (
            <Grid size={{ xs: 12, sm: 6 }} key={spec.label}>
              <Box sx={{
                py: 2.5, pr: { sm: 4 },
                borderTop: `1px solid ${project.color}15`,
              }}>
                <Typography variant="caption" sx={{ color: project.color, letterSpacing: 2, textTransform: 'uppercase', fontSize: '0.65rem' }}>
                  {spec.label}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5, fontSize: '0.95rem' }}>
                  {spec.value}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,110,180,0.08)', mx: { xs: 3, md: 10 } }} />

      {/* ── Visual Breakdown ── */}
      <Box sx={{ px: { xs: 3, md: 8 }, py: { xs: 8, md: 12 }, maxWidth: 1400, mx: 'auto' }}>
        <Typography variant="overline" sx={{ color: project.color, letterSpacing: 4, fontSize: '0.7rem' }}>
          VISUAL BREAKDOWN
        </Typography>
        <Typography variant="h2" sx={{
          fontSize: { xs: '2rem', md: '2.8rem' }, fontWeight: 700,
          letterSpacing: '-0.02em', mt: 1, mb: { xs: 6, md: 10 },
        }}>
          The Work
        </Typography>

        <Grid container spacing={{ xs: 4, md: 6 }}>
          {project.gallery.map((item, i) => (
            <Grid size={{ xs: 12, sm: 6 }} key={item.label}>
              <Box
                onClick={() => setLightbox({ src: item.image, label: item.label })}
                sx={{
                  cursor: 'zoom-in',
                  '&:hover .bd-overlay': { opacity: 1 },
                  '&:hover img': { transform: 'scale(1.02)' },
                }}
              >
                <Box sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: 1,
                  background: `${project.color}08`,
                  mb: 2,
                }}>
                  <Box
                    component="img"
                    src={item.image}
                    alt={item.label}
                    sx={{
                      display: 'block',
                      width: '100%',
                      height: 'auto',
                      transition: 'transform 0.5s ease',
                    }}
                  />
                  <Box className="bd-overlay" sx={{
                    position: 'absolute', inset: 0,
                    background: 'rgba(0,0,0,0.22)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    opacity: 0, transition: 'opacity 0.2s',
                    pointerEvents: 'none',
                  }}>
                    <ZoomInIcon sx={{ fontSize: '2rem', color: '#fff' }} />
                  </Box>
                </Box>

                <Stack direction="row" spacing={1.5} alignItems="baseline">
                  <Typography sx={{
                    fontSize: '0.65rem', fontWeight: 700,
                    color: project.color, letterSpacing: 2, textTransform: 'uppercase', flexShrink: 0,
                  }}>
                    {String(i + 1).padStart(2, '0')}
                  </Typography>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>{item.label}</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6, fontSize: '0.85rem' }}>
                      {item.desc}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,110,180,0.08)', mx: { xs: 3, md: 10 } }} />

      {/* ── Challenges ── */}
      <Box sx={{ maxWidth: 780, mx: 'auto', px: { xs: 3, md: 4 }, py: { xs: 8, md: 12 } }}>
        <Typography variant="overline" sx={{ color: project.color, letterSpacing: 4, fontSize: '0.7rem' }}>
          PROCESS
        </Typography>
        <Typography variant="h2" sx={{
          fontSize: { xs: '2rem', md: '2.8rem' }, fontWeight: 700,
          letterSpacing: '-0.02em', mt: 1, mb: { xs: 6, md: 8 },
        }}>
          Challenges & Solutions
        </Typography>
        <Stack spacing={0} divider={<Divider sx={{ borderColor: `${project.color}12` }} />}>
          {project.challenges.map((challenge, i) => (
            <Box key={challenge.title} sx={{ py: 4, display: 'flex', gap: 4 }}>
              <Typography sx={{
                fontSize: '0.7rem', fontWeight: 700, color: `${project.color}66`,
                letterSpacing: 2, pt: 0.5, flexShrink: 0, width: 28,
              }}>
                {String(i + 1).padStart(2, '0')}
              </Typography>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', mb: 1.5 }}>
                  {challenge.title}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.9 }}>
                  {challenge.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>
      </Box>

      {/* ── Footer CTA ── */}
      <Box sx={{
        textAlign: 'center',
        py: { xs: 10, md: 16 },
        px: 3,
        background: `radial-gradient(ellipse 60% 50% at 50% 100%, ${project.color}10 0%, transparent 70%)`,
      }}>
        <Typography variant="overline" sx={{ color: project.color, letterSpacing: 4, fontSize: '0.7rem' }}>
          LIKE WHAT YOU SEE?
        </Typography>
        <Typography variant="h2" sx={{
          fontSize: { xs: '2rem', md: '3rem' }, fontWeight: 700,
          letterSpacing: '-0.02em', mt: 1, mb: 4,
        }}>
          Let's work together
        </Typography>
        <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" sx={{ gap: 2 }}>
          <Button
            component={RouterLink}
            to="/#portfolio"
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            sx={{ borderColor: `${project.color}44`, color: 'text.secondary', '&:hover': { borderColor: project.color, color: project.color } }}
          >
            All Projects
          </Button>
          <Button
            variant="contained"
            href="https://discord.com/users/panekopanko"
            sx={{ background: 'linear-gradient(135deg, #ff6eb4, #b44fff)', '&:hover': { boxShadow: '0 0 24px rgba(255,110,180,0.4)' } }}
          >
            Commission Me
          </Button>
        </Stack>
      </Box>

      {/* ── Lightbox ── */}
      <Modal open={!!lightbox} onClose={() => setLightbox(null)}>
        <Box
          sx={{
            position: 'fixed', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.92)',
            p: { xs: 2, md: 4 },
          }}
          onClick={() => setLightbox(null)}
        >
          <IconButton
            onClick={() => setLightbox(null)}
            sx={{
              position: 'fixed', top: 16, right: 16,
              color: '#fff', background: 'rgba(255,255,255,0.1)',
              '&:hover': { background: 'rgba(255,255,255,0.2)' },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            component="img"
            src={lightbox?.src}
            alt={lightbox?.label}
            onClick={(e) => e.stopPropagation()}
            sx={{
              maxWidth: '100%',
              maxHeight: '90vh',
              objectFit: 'contain',
              borderRadius: 1,
              boxShadow: `0 0 80px ${project.color}33`,
            }}
          />
          {lightbox?.label && (
            <Typography sx={{
              position: 'fixed', bottom: 24,
              color: 'rgba(255,255,255,0.6)',
              fontSize: '0.85rem',
              letterSpacing: 1,
              pointerEvents: 'none',
            }}>
              {lightbox.label}
            </Typography>
          )}
        </Box>
      </Modal>
    </Box>
  );
}
